import {
  Component,
  Input,
  Output,
  EventEmitter,
  forwardRef,
  ChangeDetectorRef,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

export interface AmountFilterValue {
  amount: string;
  equalityType: 'gte' | 'eq' | 'lte';
}

@Component({
  selector: 'app-amount-filter',
  templateUrl: './app-amount-filter.component.html',
  styleUrls: ['./app-amount-filter.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => AppAmountFilterComponent),
      multi: true,
    },
  ],
})
export class AppAmountFilterComponent implements ControlValueAccessor {
  @Input() labelText: string = 'Due Amount';
  @Input() label: string = '';

  get resolvedLabel(): string {
    return this.label || this.labelText || 'Due Amount';
  }
  @Input() currencySymbol: string = '₹';
  @Input() disabled: boolean = false;

  @Output() filterChange = new EventEmitter<AmountFilterValue | null>();
  @Output() clear = new EventEmitter<void>();

  amount: string = '';
  tempAmount: string = '';
  equalityType: 'gte' | 'eq' | 'lte' = 'eq';
  tempEqualityType: 'gte' | 'eq' | 'lte' = 'eq';
  isOpen: boolean = false;

  equalityOptions = [
    { label: 'is greater than & equals to', value: 'gte', symbol: '>=' },
    { label: 'is equal to', value: 'eq', symbol: '=' },
    { label: 'is lesser than & equals to', value: 'lte', symbol: '<=' },
  ];

  private onChange: (val: AmountFilterValue | null) => void = () => {};
  private onTouched: () => void = () => {};

  constructor(private cdr: ChangeDetectorRef) {}

  writeValue(val: AmountFilterValue | null): void {
    if (val) {
      this.amount = val.amount || '';
      this.equalityType = val.equalityType || 'eq';
    } else {
      this.amount = '';
      this.equalityType = 'eq';
    }
    this.tempAmount = this.amount;
    this.tempEqualityType = this.equalityType;
    this.cdr.markForCheck();
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
    this.cdr.markForCheck();
  }

  get isFilterApplied(): boolean {
    return !!this.amount && this.amount.trim().length > 0;
  }

  get equalitySymbol(): string {
    const match = this.equalityOptions.find(
      (opt) => opt.value === this.equalityType
    );
    return match ? match.symbol : '=';
  }

  get displayAmount(): string {
    if (!this.amount) return '';
    const num = parseFloat(this.amount);
    return isNaN(num)
      ? this.amount
      : num.toLocaleString('en-IN', { maximumFractionDigits: 2 });
  }

  toggleDropdown(event?: Event): void {
    if (this.disabled) return;
    if (event) event.stopPropagation();
    this.isOpen = !this.isOpen;
    if (this.isOpen) {
      this.tempAmount = this.amount;
      this.tempEqualityType = this.equalityType;
    }
  }

  closeDropdown(): void {
    this.isOpen = false;
    this.onTouched();
  }

  applyFilter(event?: Event): void {
    if (event) event.stopPropagation();
    if (!this.tempAmount.trim()) return;

    this.amount = this.tempAmount.trim();
    this.equalityType = this.tempEqualityType;

    const payload: AmountFilterValue = {
      amount: this.amount,
      equalityType: this.equalityType,
    };

    this.onChange(payload);
    this.filterChange.emit(payload);
    this.closeDropdown();
  }

  handleCancelFilter(event: Event): void {
    event.stopPropagation();
    if (!this.isFilterApplied) return;
    this.amount = '';
    this.tempAmount = '';
    this.equalityType = 'eq';
    this.tempEqualityType = 'eq';
    this.onChange(null);
    this.filterChange.emit(null);
    this.clear.emit();
    this.closeDropdown();
  }
}
